/**
 * ShellSessionRunner — a raw login shell relayed as a terminal tab. Spawned as
 * a same-pod child (`session:spawnTui` with mode "shell") so a human can poke
 * around the workspace manually from the card's App/terminal tabs.
 *
 * The sibling of AdhocSessionRunner minus the Claude harness: connect →
 * register → spawn `$SHELL -l` under node-pty in the workspace checkout →
 * relay raw bytes over the S2 relay (ptyOutput/ptyInput/ptyResize). The tab
 * ends when the human exits the shell, the roster kill sends session:stop, or
 * the pod goes down. Idle-sleep stays server-owned; the runner's own idle
 * timer is the backstop, re-armed on every keystroke so an actively-used
 * shell never trips it.
 */
import { AgentConnection, type AgentConnectionConfig } from "../connection/agent-connection.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import {
  loadPtySpawn,
  inheritedEnv,
  type PtyProcess,
  type PtySpawn,
} from "../harness/pty/pty-support.js";

const DEFAULT_COLS = 80;
const DEFAULT_ROWS = 24;

export interface ShellSessionRunnerConfig {
  connection: AgentConnectionConfig;
  /** Repo checkout the shell starts in. */
  workspaceDir: string;
  /** Shell binary override; falls back to $SHELL, then bash. */
  shell?: string;
  lifecycle?: Partial<LifecycleConfig>;
}

/** AgentConnection surface the shell runner needs (injectable for tests). */
export interface ShellRunnerConnection {
  connect(): Promise<void>;
  disconnect(): void;
  call(method: "connectAgent", payload: { sessionId: string }): Promise<unknown>;
  sendEvent(event: { type: string; [key: string]: unknown }): void;
  sendHeartbeat(): void;
  emitStatus(status: string): Promise<void>;
  onStop(callback: () => void): void;
  refreshTaskTokenFromBootstrap(): Promise<boolean>;
  sendPtyOutput(data: string, dims?: { cols: number; rows: number }): void;
  sendPtyEnded(): void;
  onPtyInput(handler: (data: string) => void): () => void;
  onPtyResize(handler: (cols: number, rows: number) => void): () => void;
}

/**
 * The shell's env: the pod env minus this session's Conveyor identity. The
 * human at the prompt doesn't need the session JWT sitting in `env` output —
 * and a copied token must not leak task authority into pasted shell logs.
 */
export function buildShellEnv(baseEnv: Record<string, string>): Record<string, string> {
  const env = { ...baseEnv };
  delete env.CONVEYOR_TASK_TOKEN;
  delete env.CONVEYOR_SESSION_ID;
  return env;
}

export class ShellSessionRunner {
  readonly connection: ShellRunnerConnection;
  readonly lifecycle: Lifecycle;

  private readonly config: ShellSessionRunnerConfig;
  private readonly loadSpawn: () => Promise<PtySpawn>;
  private pty: PtyProcess | null = null;
  private dims = { cols: DEFAULT_COLS, rows: DEFAULT_ROWS };
  private stopped = false;
  private _finalState: "finished" | "error" | null = null;

  constructor(
    config: ShellSessionRunnerConfig,
    deps?: { connection?: ShellRunnerConnection; loadSpawn?: () => Promise<PtySpawn> },
  ) {
    this.config = config;
    this.connection = deps?.connection ?? new AgentConnection(config.connection);
    this.loadSpawn = deps?.loadSpawn ?? loadPtySpawn;
    this.lifecycle = new Lifecycle(
      // No git flush — the human commits/pushes explicitly from the shell.
      { ...DEFAULT_LIFECYCLE_CONFIG, gitFlushIntervalMs: 0, ...config.lifecycle },
      {
        onHeartbeat: () => this.connection.sendHeartbeat(),
        onIdleTimeout: () => this.requestStop(),
        onDormantTimeout: () => this.requestStop(),
        onTokenRefresh: () => void this.connection.refreshTaskTokenFromBootstrap().catch(() => {}),
        onGitFlush: () => {},
        onUsageSample: () => {},
      },
    );
  }

  get finalState(): "finished" | "error" | null {
    return this._finalState;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Connect, register, spawn the shell, and relay it until exit or stop. */
  async run(): Promise<void> {
    try {
      await this.connection.connect();
      this.connection.sendEvent({
        type: "connected",
        sessionId: this.config.connection.sessionId,
      });
      this.connection.onStop(() => this.requestStop());
      this.lifecycle.startHeartbeat();
      this.lifecycle.startTokenRefresh();

      await this.connection.call("connectAgent", {
        sessionId: this.config.connection.sessionId,
      });
      await this.connection.emitStatus("running");
      this.lifecycle.startIdleTimer();

      await this.runShell();
      this._finalState = "finished";
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      process.stderr.write(`[conveyor-agent] Shell runner failed: ${message}\n`);
      this.connection.sendEvent({ type: "error", message });
      this._finalState = "error";
    } finally {
      this.shutdown();
    }
  }

  /** Spawn the login shell under a PTY and block until it exits or stop(). */
  private async runShell(): Promise<void> {
    const spawn = await this.loadSpawn();
    const shell = this.config.shell ?? process.env.SHELL ?? "bash";
    const pty = spawn(shell, ["-l"], {
      name: "xterm-256color",
      cols: this.dims.cols,
      rows: this.dims.rows,
      cwd: this.config.workspaceDir,
      env: buildShellEnv(inheritedEnv()),
    });
    this.pty = pty;

    const unsubInput = this.connection.onPtyInput((data) => {
      // A keystroke is real human activity — re-arm the idle backstop.
      this.lifecycle.startIdleTimer();
      pty.write(data);
    });
    const unsubResize = this.connection.onPtyResize((cols, rows) => {
      this.dims = { cols, rows };
      pty.resize(cols, rows);
    });
    pty.onData((data) => this.connection.sendPtyOutput(data, this.dims));

    try {
      await new Promise<void>((resolve) => {
        pty.onExit(() => resolve());
      });
    } finally {
      unsubInput();
      unsubResize();
      this.pty = null;
      // The shell is gone and no respawn is coming — hide the tab now instead
      // of waiting for the lease to lapse.
      this.connection.sendPtyEnded();
    }
  }

  stop(): void {
    this.requestStop();
  }

  private requestStop(): void {
    if (this.stopped) return;
    this.stopped = true;
    try {
      this.pty?.kill();
    } catch {
      /* already gone */
    }
  }

  private shutdown(): void {
    this.stopped = true;
    this.lifecycle.destroy();
    this.connection.sendEvent({ type: "shutdown", reason: this._finalState ?? "finished" });
    this.connection.disconnect();
  }
}
